import Vue from 'vue'
import Router from 'vue-router'
import Store from '@/store'
import axios from 'axios'

Vue.use(Router)

// When building apps with a bundler, the JavaScript bundle can become quite large, and thus affect the page load time.
// It would be more efficient if we can split each route's components into a separate chunk, and only load them when the route is visited.
// The loadView function makes use of Vue's async component feature and webpack's code splitting to achieve this.
// More information: https://router.vuejs.org/guide/advanced/lazy-loading.html

// There is a case of pop-in when lazy loading the routes, it needs to be fixed

function loadView (folder, view) {
  return () => import(/* webpackChunkName: "[request]" */ `@/views/${folder}/${view}.vue`)
}

const router = new Router({
  mode: 'history',
  base: '/',
  routes: [
    {
      path: '/register',
      name: 'register',
      component: loadView('user', 'Register')
    },
    {
      path: '/register/:token',
      name: 'register-token',
      component: loadView('user', 'Register')
    },
    {
      path: '/login',
      name: 'login',
      component: loadView('user', 'Login')
    },
    // {
    //   path: '/login/:registered',
    //   name: 'login-registered',
    //   component: loadView('user', 'Login')
    // },
    {
      path: '/login/:token',
      name: 'login-publisher-invite',
      component: loadView('user', 'Login')
    },
    {
      path: '/forgot-password',
      name: 'forgot-password',
      component: loadView('user', 'Forgot-password')
    },
    {
      path: '/reset-password/:token',
      name: 'reset-password',
      component: loadView('user', 'Reset-password')
    },
    {
      path: '/user/edit',
      name: 'user-edit',
      component: loadView('user', 'Edit')
    },
    {
      path: '/user/:user/edit',
      name: 'admin-user-edit',
      component: loadView('user', 'Edit')
    },
    {
      path: '/company/edit',
      name: 'company-edit',
      component: loadView('organization', 'Edit')
    },
    {
      path: '/company/:company/edit',
      name: 'admin-company-edit',
      component: loadView('organization', 'Edit')
    },
    {
      path: '/company/detail',
      name: 'company-detail',
      component: loadView('organization', 'Detail')
    },
    {
      path: '/company/apply-to-publisher',
      name: 'company-apply-to-publisher',
      component: loadView('organization', 'Apply')
    },
    {
      path: '/company/apply-to-publisher/:token',
      name: 'company-apply-to-publisher-with-token',
      component: loadView('organization', 'Apply')
    },
    {
      path: '/company/manage-jobs',
      name: 'company-manage-jobs',
      component: loadView('organization', 'Manage-jobs')
    },
    {
      path: '/job/create',
      name: 'job-create',
      component: loadView('job', 'Create')
    },
    {
      path: '/job/browse',
      name: 'job-list',
      component: loadView('job', 'List')
    },
    {
      path: '/job/:job',
      name: 'job-detail',
      component: loadView('job', 'Detail')
    },
    {
      path: '/job/:job/edit',
      name: 'job-edit',
      component: loadView('job', 'Create')
    },
    {
      path: '/publisher/edit',
      name: 'publisher-edit',
      component: loadView('publisher', 'Edit')
    },
    {
      path: '/publisher/:publisher/edit',
      name: 'admin-publisher-edit',
      component: loadView('publisher', 'Edit')
    },
    {
      path: '/publisher/manage-organisations',
      name: 'publisher-manage-organisations',
      component: loadView('publisher', 'Manage-organisations')
    },
    {
      path: '/publisher/manage-jobs',
      name: 'publisher-manage-jobs',
      component: loadView('publisher', 'Manage-jobs')
    },
    {
      path: '/publisher/invite/:token',
      name: 'publisher-invite-organisation'
      // redirect: '/company/apply-to-publisher/:token'
    },
    {
      path: '/admin/manage-users',
      name: 'admin-manage-users',
      component: loadView('admin', 'Manage-users')
    },
    {
      path: '/admin/manage-organisations',
      name: 'admin-manage-organisations',
      component: loadView('admin', 'Manage-organisations')
    },
    {
      path: '/admin/manage-publishers',
      name: 'admin-manage-publishers',
      component: loadView('admin', 'Manage-publishers')
    },
    {
      path: '/admin/manage-jobs',
      name: 'admin-manage-jobs',
      component: loadView('organization', 'Manage-jobs')
    },
    {
      path: '/site-notice',
      name: 'site-notice',
      component: loadView('misc', 'Site-notice')
    },
    {
      path: '/privacy-policy',
      name: 'privacy-policy',
      component: loadView('misc', 'Privacy-policy')
    },
    // This is the wildcard route used when the browser is directed to any route not defined above,
    // for now it's just the register one but it should be changed to a custom 404 component.
    {
      path: '*',
      redirect: '/login'
    }
  ]
})

const userLoad = async function () {
  await Store.dispatch('auth/populate')
}

const companyLoad = async function (id) {
  await Store.dispatch('company/populate', id)
}

const publisherLoad = async function (id) {
  await Store.dispatch('company/populatePublisher', id)
}

router.beforeEach(async (to, from, next) => {
  const isLoggedIn = Store.getters['auth/isLoggedIn']
  const currentUser = Store.getters['auth/currentUser']
  // const isAdmin = Store.getters['auth/isAdmin']
  const path = to.path.toLowerCase()

  await Store.dispatch('auth/setStatus', { loading: false, error: false, message: '' })
  await Store.dispatch('company/setStatus', { loading: false, error: false, message: '' })

  if (to.path === '/publisher/invite/' + to.params.token) {
    if (isLoggedIn) {
      next({ path: '/company/apply-to-publisher/' + to.params.token })
    } else {
      next({ path: '/login/' + to.params.token })
    }
  } else {
    switch (path) {
      case '/register':
        next(isLoggedIn ? { path: '/user/edit' } : true)
        break
      case '/register/' + to.params.token :
        next(isLoggedIn ? { path: '/user/edit' } : true)
        break
      case '/login':
        next(isLoggedIn ? { path: '/user/edit' } : true)
        break
      case '/forgot-password':
        next(isLoggedIn ? { path: '/user/edit' } : true)
        break
      case '/user/edit':
        next(isLoggedIn ? userLoad() : { path: '/login' })
        break
      case '/user/' + to.params.user + '/edit':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/company/edit':
        next(isLoggedIn ? companyLoad(currentUser.organisation_id) : { path: '/login' })
        break
      case '/company/' + to.params.company + '/edit':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/company/detail':
        next(isLoggedIn ? companyLoad(currentUser.organisation_id) : { path: '/login' })
        break
      case '/company/apply-to-publisher':
        next(isLoggedIn ? companyLoad(currentUser.organisation_id) : { path: '/login' })
        break
      case '/company/manage-jobs':
        next(isLoggedIn ? true : { path: '/login' })
        break
      case '/job/create':
        next(isLoggedIn ? true : { path: '/login' })
        break
      case '/job/' + to.params.job + '/edit':
        // I have to do something here, since the ids of the jobs are in the url people can try and guess ids and the way it was
        // before it would have lead them to the edit page of that job
        // I have to verify if the job belongs to the user's organisation or if the user is an admin
        // I have no other way of knowing that at the moment, I have to make a query here and compare the organisation ids.
        const endpoint = process.env.VUE_APP_BACKEND + '/job/' + to.params.job
        let job = (await axios.get(endpoint)).data
        next(isLoggedIn && ((job.organisation || {}).id === currentUser.organisation_id || currentUser.type === 'admin') ? true : { path: '/login' })
        break
      case '/job/browse':
        next(true)
        break
      case '/publisher/edit':
        next(isLoggedIn && currentUser.type === 'publisher' ? publisherLoad(currentUser.publisher_id) : { path: '/login' })
        break
      case '/publisher/' + to.params.publisher + 'edit':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/publisher/manage-organisations':
        next(isLoggedIn && currentUser.type === 'publisher' ? publisherLoad(currentUser.publisher_id) : { path: '/login' })
        break
      case '/publisher/manage-jobs':
        next(isLoggedIn && currentUser.type === 'publisher' ? publisherLoad(currentUser.publisher_id) : { path: '/login' })
        break
      // case '/publisher/invite/' + to.params.token:
      //   This doesn't work because the router never finds a match for a route where the token is all lowercase.
      //   next(isLoggedIn ? { path: '/job/browse' } : { path: '/publisher/edit' })
      //   break
      case '/admin/manage-users':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/admin/manage-organisations':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/admin/manage-publishers':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      case '/admin/manage-jobs':
        next(isLoggedIn && currentUser.type === 'admin' ? true : { path: '/login' })
        break
      default:
        next()
    }
  }
})

export default router
